/**
 * PdfViewerViewEntry — code-split boundary + error/suspense fallback for
 * PdfViewerView, which pulls in the ~MB `pdfjs-dist` chunk. This is what the
 * MAIN_VIEWS registry points at; pdf.js only loads when the user first opens a
 * presentation/PDF.
 *
 * Mirrors DocxEditorViewEntry: an error boundary catches chunk-load failures
 * (network drop, stale asset after redeploy) and runtime errors from a
 * malformed PDF, showing a minimal recovery UI instead of a blank slot.
 */

import { Component, lazy, Suspense, type ReactNode } from 'react';

const PdfViewerView = lazy(() => import('./PdfViewerView'));

interface EntryProps {
  path?: string;
  sessionId: string | null;
  onClose: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class PdfErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error) {
    console.error('[PdfViewerView] boundary caught:', error);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="pdf-view pdf-view--message">
          <div className="pdf-view-message-title">Не удалось открыть документ</div>
          <div className="pdf-view-message-sub">{this.state.error.message}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function PdfViewerViewEntry(props: EntryProps) {
  if (!props.path) {
    return (
      <div className="pdf-view pdf-view--message">
        <div className="pdf-view-message-title">PdfViewerView requires a `path` prop.</div>
      </div>
    );
  }

  return (
    <PdfErrorBoundary onClose={props.onClose}>
      <Suspense fallback={<div className="pdf-view pdf-view--message">Загрузка просмотра…</div>}>
        <PdfViewerView
          path={props.path}
          sessionId={props.sessionId}
          onClose={props.onClose}
        />
      </Suspense>
    </PdfErrorBoundary>
  );
}
